import { useEffect, useState } from "react";
import SignaturePad from "./SignaturePad";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PenLine, Trash2, ShieldCheck, Download } from "lucide-react";

const WAIVER_TEXT = `I voluntarily waive my right to Ontario Employment Standards Act vacation
pay accrual and payout. I acknowledge that in exchange I have accepted wage
terms agreed with Elite Mechanical that provide sufficient consideration
for this waiver. I understand no vacation pay will accrue while this
waiver is in effect, and I can revoke this waiver in writing at any time
by contacting the employer. I confirm I am signing this document freely
and understand its effect.`;

const VacationWaiverDialog = ({ open, employee, onClose, onSaved, endpoints }) => {
  // Default admin endpoints; a self-service caller passes /me/* URLs.
  const eps = endpoints || {
    sign: `/employees/${employee?.employee_id}/vacation-waiver`,
    revoke: `/employees/${employee?.employee_id}/vacation-waiver`,
    pdf: `/employees/${employee?.employee_id}/vacation-waiver/pdf`,
  };
  const [signature, setSignature] = useState("");
  const [signedName, setSignedName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSignature("");
      setSignedName(employee?.name || "");
      setAck(false);
      // Blur any input the underlying employee dialog had focused so
      // Android's soft keyboard collapses and its stray text-cursor stops
      // aiming taps at the field behind us.
      try {
        const el = document.activeElement;
        if (el && typeof el.blur === "function" && el !== document.body) el.blur();
      } catch (_) { /* ignore */ }
    }
  }, [open, employee]);

  if (!open || !employee) return null;

  const existing = employee?.vacation_waiver;

  const sign = async () => {
    if (!signedName.trim()) return toast.error("Type your printed name");
    if (!signature) return toast.error("Draw your signature");
    if (!ack) return toast.error("Please acknowledge the waiver");
    setBusy(true);
    try {
      const { data } = await api.post(eps.sign, {
        signature,
        signed_name: signedName.trim(),
      });
      toast.success("Waiver signed");
      // Self-service returns {employee}; admin returns Employee directly.
      onSaved?.(data?.employee || data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save waiver");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke the signed vacation-pay waiver? Vacation pay will start accruing again.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(eps.revoke);
      toast.success("Waiver revoked");
      onSaved?.(data?.employee || data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke waiver");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(eps.pdf, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vacation_waiver_${(employee.name || "employee").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Could not download waiver"); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-3"
      data-testid="vacation-waiver-dialog"
      // Only stop click bubbling so a tap on the backdrop doesn't leak to
      // the employee dialog underneath. touch/pointer stopPropagation on
      // the parent was swallowing the child button clicks on some Android
      // browsers (Samsung Internet, older Chrome) — leaving X and Revoke
      // completely unresponsive.
      onClick={(ev) => ev.stopPropagation()}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Vacation-pay waiver</p>
            <h3 className="truncate text-lg font-bold uppercase tracking-tight">
              {existing ? "On file · " : ""}{employee.name}
            </h3>
          </div>
          <button
            onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); onClose(); }}
            data-testid="vacation-waiver-close"
            type="button"
            className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[75vh] space-y-4 overflow-y-auto p-4">
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 text-[12px] leading-relaxed text-white/80">
            {WAIVER_TEXT}
          </div>

          {existing ? (
            <div className="rounded-md border border-emerald-800/40 bg-emerald-800/10 p-3">
              <p className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5" /> Signed waiver on file
              </p>
              <div className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
                <p><span className="text-white/50">Printed name:</span> {existing.signed_name}</p>
                <p><span className="text-white/50">Signed at:</span> {existing.signed_at?.slice(0, 19).replace("T", " ")}</p>
              </div>
              {existing.signature && (
                <img alt="signature"
                  src={existing.signature}
                  className="mt-3 h-24 rounded border border-white/10 bg-white p-1" />
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={downloadPdf} data-testid="vacation-waiver-pdf-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:border-[#1a3fd1] hover:text-white">
                  <Download className="h-3.5 w-3.5" /> Download PDF
                </button>
                <button onClick={revoke} disabled={busy} data-testid="vacation-waiver-revoke-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-300 hover:border-red-500/60 disabled:opacity-50">
                  <Trash2 className="h-3.5 w-3.5" /> Revoke waiver
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Printed name (type exactly as signed)</label>
                <input
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  data-testid="vacation-waiver-name"
                  className="mt-1 h-9 w-full rounded border border-white/15 bg-white/5 px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                />
              </div>

              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white/80">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="vacation-waiver-ack"
                  className="mt-0.5 h-4 w-4 rounded border-white/30 bg-black/40 accent-[#1a3fd1]"
                />
                <span>
                  I confirm I&apos;ve read the waiver above and I&apos;m signing it voluntarily.
                </span>
              </label>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/50">Signature</label>
                <SignaturePad open={open} onChange={setSignature} testid="vacation-waiver-signature" />
              </div>
            </>
          )}
        </div>

        {!existing && (
          <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
            <button onClick={onClose}
              className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70">
              Cancel
            </button>
            <button
              onClick={sign} disabled={busy}
              data-testid="vacation-waiver-sign-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50">
              <PenLine className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Sign waiver"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VacationWaiverDialog;
